import { Box } from "@mui/material";
import { differenceInMinutes } from "date-fns";
import { useMemo } from "react";
import { AppointmentExtendedEventProps } from "@/types/calendarEventType";

type ServiceIndicator = {
  type: "service" | "break";
  heightPercentage: number;
  backgroundColor: string;
  serviceId: string;
};

type AppointmentServiceProviderIndicatorProps = {
  start: Date;
  end: Date;
  appointmentServices: AppointmentExtendedEventProps["appointmentServices"];
};

const AppointmentServiceProviderIndicator = ({
  start,
  end,
  appointmentServices,
}: AppointmentServiceProviderIndicatorProps) => {
  const indicators = useMemo(() => {
    const totalMinutesDuration = differenceInMinutes(end, start);
    const indicators: ServiceIndicator[] = [];

    appointmentServices.forEach((service, idx) => {
      const serviceMinutesDuration = differenceInMinutes(
        new Date(service.endTime),
        new Date(service.startTime)
      );

      const prevService = appointmentServices[idx - 1];
      const breakMinutesDuration = prevService
        ? differenceInMinutes(
            new Date(service.startTime),
            new Date(prevService.endTime)
          )
        : 0;

      // if there is a break between services we want to show it as unassigned indicator
      if (breakMinutesDuration > 0) {
        indicators.push({
          type: "break",
          heightPercentage: (breakMinutesDuration / totalMinutesDuration) * 100,
          backgroundColor: "transparent",
          serviceId: service.id,
        });
      }

      indicators.push({
        type: "service",
        heightPercentage: (serviceMinutesDuration / totalMinutesDuration) * 100,
        backgroundColor: service.providerColorPalette[80],
        serviceId: service.id,
      });
    });

    return indicators;
  }, [appointmentServices, start, end]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: "-6px",
        bottom: "-6px",
        left: "-8px",
        width: "6px",
      }}
    >
      {indicators.map((indicator, index) => {
        if (indicator.type === "service") {
          return (
            <ServiceIndicator
              key={`${indicator.serviceId}-${indicator.type}`}
              backgroundColor={indicator.backgroundColor}
              height={`${indicator.heightPercentage}%`}
              isLast={index === indicators.length - 1}
            />
          );
        }

        return (
          <ServiceIndicator
            key={`${indicator.serviceId}-${indicator.type}`}
            backgroundColor={indicator.backgroundColor}
            height={`calc(${indicator.heightPercentage}% - 1px)`}
            isLast={index === indicators.length - 1}
          />
        );
      })}
    </Box>
  );
};

const ServiceIndicator = ({
  backgroundColor,
  height,
  isLast,
}: {
  backgroundColor: string;
  height: string;
  isLast: boolean;
}) => (
  <Box
    sx={{
      background: backgroundColor,
      height,
      width: "100%",
      mb: isLast ? "0px" : "1px",
    }}
  />
);

export default AppointmentServiceProviderIndicator;
